import React from 'react';
import { Car, Building2, Info, Minus, Plus, Users } from 'lucide-react';
import { RangeSliderField } from '../shared/RangeSliderField';

const Stepper = ({ label, hint, value, min, max, onChange, testId }) => (
  <div className="flex flex-col h-full">
    <label className="text-[11px] text-stone-500 block leading-tight mb-2 min-h-[2rem]">
      <span className="block">{label}</span>
      {hint && <span className="block text-stone-500 text-[10px] mt-0.5">{hint}</span>}
    </label>
    <div className="mt-auto flex items-center justify-between rounded-full border border-stone-200 px-1 py-1">
      <button
        type="button"
        onClick={() => onChange(Math.max(min, value - 1))}
        className="w-8 h-8 rounded-full flex items-center justify-center text-stone-500 hover:text-stone-900 hover:bg-stone-100 transition-colors"
        data-testid={`${testId}-minus`}
        aria-label="Weniger"
      >
        <Minus className="w-3.5 h-3.5" strokeWidth={2} />
      </button>
      <span className="text-base font-medium text-stone-900 tabular-nums" data-testid={`${testId}-value`}>{value}</span>
      <button
        type="button"
        onClick={() => onChange(Math.min(max, value + 1))}
        className="w-8 h-8 rounded-full flex items-center justify-center text-stone-500 hover:text-stone-900 hover:bg-stone-100 transition-colors"
        data-testid={`${testId}-plus`}
        aria-label="Mehr"
      >
        <Plus className="w-3.5 h-3.5" strokeWidth={2} />
      </button>
    </div>
  </div>
);

/** Rechte Konfigurator-Spalte, Schritte 1+2: Variante + Slider. */
export const ConfiguratorControls = ({
  mode, lockMode, isPvt, vehicles, annualKm, employees,
  householdAdults = 1, householdVehicles = 1,
  onVehicles, onAnnualKm, onEmployees, onSwitchMode,
  onHouseholdAdults, onHouseholdVehicles,
}) => (
  <>
    {/* 1 · Variante */}
    {!lockMode && (
      <div className="mb-7" data-testid="configurator-variants">
        <p className="text-[10px] font-medium uppercase tracking-[0.2em] text-stone-500 mb-2.5">1 · Variante</p>
        <div className="grid grid-cols-2 gap-1 p-1 bg-stone-100 rounded-full">
          {[
            ['private', 'Privat', <Car key="p" className="w-3.5 h-3.5" strokeWidth={2} />],
            ['business', 'Business', <Building2 key="b" className="w-3.5 h-3.5" strokeWidth={2} />],
          ].map(([m, l, icon]) => (
            <button
              key={m}
              onClick={() => onSwitchMode(m)}
              className={`flex items-center justify-center gap-2 py-2.5 rounded-full text-sm font-medium transition-colors ${
                mode === m ? 'bg-stone-900 text-white' : 'text-stone-500 hover:text-stone-900'
              }`}
              data-testid={`configurator-variant-${m}`}
            >
              {icon}{l}
            </button>
          ))}
        </div>
      </div>
    )}

    {/* 2 · Konfiguration */}
    <div className="space-y-6 mb-7">
      <p className="text-[10px] font-medium uppercase tracking-[0.2em] text-stone-500">2 · Konfiguration</p>

      {isPvt && (
        <>
          <div className="grid grid-cols-2 gap-3 items-stretch">
            <Stepper
              label="Personen im Haushalt"
              hint="ab 18 Jahren"
              value={householdAdults}
              min={1}
              max={4}
              onChange={onHouseholdAdults}
              testId="configurator-adults"
            />
            <Stepper
              label="Fahrzeuge im Haushalt"
              hint="je Fahrzeug ein Halter"
              value={householdVehicles}
              min={1}
              max={4}
              onChange={onHouseholdVehicles}
              testId="configurator-hh-vehicles"
            />
          </div>
          <div className="rounded-xl bg-gradient-to-r from-emerald-50 via-teal-50/70 to-emerald-50/40 border border-emerald-200/80 shadow-[inset_0_1px_0_rgba(255,255,255,0.85)] px-3.5 py-3 flex items-start gap-2.5" data-testid="configurator-household-info">
            <span className="w-7 h-7 rounded-full bg-white border border-emerald-200 flex items-center justify-center flex-shrink-0 shadow-sm">
              <Users className="w-3.5 h-3.5 text-emerald-600" strokeWidth={2} />
            </span>
            <p className="text-[11px] leading-relaxed text-emerald-900">
              <strong className="font-semibold">Bis zu 4 Personen im Haushalt inklusive</strong> — gleicher Preis, gleiches Guthaben. Mitglieder & Fahrzeuge fügst du nach dem Kauf im Account hinzu.
            </p>
          </div>
        </>
      )}

      {!isPvt && (
        <RangeSliderField
          label="Fahrzeuge in deiner Flotte"
          value={Math.min(vehicles, 201)}
          min={1} max={201} step={1} stepBy={1}
          displayValue={vehicles > 200 ? '201+' : vehicles.toLocaleString('de-DE')}
          onValueChange={onVehicles}
          minLabel="1" maxLabel="201+"
          testId="configurator-vehicles"
        />
      )}

      <RangeSliderField
        label={isPvt ? 'Kilometer / Jahr' : 'km / Fzg / Jahr'}
        value={annualKm}
        min={5000} max={isPvt ? 40000 : 50000} step={1000} stepBy={1000}
        unit="km"
        onValueChange={onAnnualKm}
        minLabel="5.000" maxLabel={isPvt ? '40.000' : '50.000'}
        testId="configurator-km"
      />

      {/* Kraftstoffkosten-Kachel liegt jetzt im Total-Block (über dem Value-Stack) */}

      {!isPvt && (
        <RangeSliderField
          label="Mitarbeiter"
          hint={`min. ${vehicles}`}
          value={Math.min(Math.max(employees, vehicles), 2000)}
          min={vehicles} max={2000} step={1} stepBy={10}
          onValueChange={onEmployees}
          minLabel={vehicles.toLocaleString('de-DE')} maxLabel="2.000+"
          testId="configurator-employees"
        />
      )}

      {vehicles > 200 && !isPvt && (
        <p className="text-[11px] text-amber-600 flex items-center gap-1" data-testid="configurator-fleet-hint">
          <Info className="w-3 h-3" strokeWidth={2} /> Ab 201 Fahrzeugen: Individuelle Konditionen — sende uns unten direkt deine Flotten-Anfrage
        </p>
      )}
    </div>
  </>
);
